import React from 'react';
import styled from 'styled-components';

import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';
import { useQrShares } from '../../../hooks/useApi';
import type {
  DomainShareScope,
  QrShare,
  QrShareStatus,
  ShareScope,
} from '../../../types/api';
import { SHARE_SCOPE_SHORT_LABELS } from '../lib/shareScopes';
import { QrShareDetailModal } from './QrShareDetailModal';
import {
  QR_SHARE_STATUS_LABELS,
  QrShareStatusBadge,
} from './QrShareStatusBadge';

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-md);
  margin-bottom: var(--space-lg);
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
`;

const Select = styled.select`
  min-height: 40px;
  padding: 0 var(--space-sm);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: var(--radius-sm, 6px);
  background: var(--surface, #fff);
  color: var(--text-primary);
`;

const StateCard = styled(Card)`
  padding: var(--space-3xl);
  text-align: center;
`;

const StateMessage = styled.p`
  margin: 0 0 var(--space-md);
  color: var(--text-secondary);
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
`;

const Row = styled.li`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-sm);
  padding: var(--space-md);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: var(--radius-md, 8px);
  background: var(--surface, #fff);
`;

const RowMain = styled.div`
  min-width: 0;
  flex: 1 1 18rem;
`;

const RowTitle = styled.p`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  flex-wrap: wrap;
  margin: 0;
  color: var(--text-primary);
  font-weight: 600;
  overflow-wrap: anywhere;
`;

const RowMeta = styled.p`
  margin: 4px 0 0;
  color: var(--text-muted);
  font-size: var(--font-size-sm);
`;

const STATUS_FILTERS: readonly QrShareStatus[] = [
  'active',
  'expired',
  'revoked',
  'archived',
];

const SCOPE_FILTERS: readonly DomainShareScope[] = [
  'all_compliance',
  'all_sales',
  'all_invoices',
  'all_reports',
];

export const QrSharesListView: React.FC = () => {
  const [status, setStatus] = React.useState<QrShareStatus | ''>('');
  const [scope, setScope] = React.useState<DomainShareScope | ''>('');
  const [openShareId, setOpenShareId] = React.useState<string | null>(null);

  const params = React.useMemo(
    () => ({
      ...(status ? { status } : {}),
      ...(scope ? { share_scope: scope as ShareScope } : {}),
    }),
    [scope, status],
  );
  const { data, isLoading, error, refetch } = useQrShares(params);
  const shares = data?.items ?? [];
  const hasFilters = Boolean(status || scope);

  return (
    <div>
      <Filters>
        <Field htmlFor="qr-share-status-filter">
          Estado
          <Select
            id="qr-share-status-filter"
            value={status}
            onChange={(event) =>
              setStatus(event.target.value as QrShareStatus | '')
            }
          >
            <option value="">Todos</option>
            {STATUS_FILTERS.map((value) => (
              <option key={value} value={value}>
                {QR_SHARE_STATUS_LABELS[value]}
              </option>
            ))}
          </Select>
        </Field>

        <Field htmlFor="qr-share-scope-filter">
          Alcance
          <Select
            id="qr-share-scope-filter"
            value={scope}
            onChange={(event) =>
              setScope(event.target.value as DomainShareScope | '')
            }
          >
            <option value="">Todos</option>
            {SCOPE_FILTERS.map((value) => (
              <option key={value} value={value}>
                {SHARE_SCOPE_SHORT_LABELS[value]}
              </option>
            ))}
          </Select>
        </Field>
      </Filters>

      {isLoading ? (
        <StateCard>
          <LoadingSpinner message="Cargando paquetes compartidos..." />
        </StateCard>
      ) : error ? (
        <StateCard role="alert">
          <StateMessage>
            No se pudieron cargar los paquetes compartidos.
          </StateMessage>
          <Button variant="primary" size="sm" onClick={() => refetch()}>
            Reintentar
          </Button>
        </StateCard>
      ) : shares.length === 0 ? (
        <StateCard>
          <StateMessage>
            {hasFilters
              ? 'No hay paquetes que coincidan con los filtros.'
              : 'Aún no has creado ningún QR compartido.'}
          </StateMessage>
        </StateCard>
      ) : (
        <List>
          {shares.map((share) => (
            <Row key={share.id}>
              <RowMain>
                <RowTitle>
                  {share.name}
                  <QrShareStatusBadge status={share.status} />
                </RowTitle>
                <RowMeta>{formatMeta(share)}</RowMeta>
              </RowMain>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setOpenShareId(share.id)}
              >
                Gestionar
              </Button>
            </Row>
          ))}
        </List>
      )}

      {openShareId && (
        <QrShareDetailModal
          shareId={openShareId}
          onClose={() => setOpenShareId(null)}
        />
      )}
    </div>
  );
};

function formatMeta(share: QrShare): string {
  const scopeLabel =
    share.share_scope === 'custom'
      ? 'Personalizado'
      : SHARE_SCOPE_SHORT_LABELS[share.share_scope];

  const parts = [
    scopeLabel,
    `${share.available_item_count}/${share.item_count} archivos`,
    `${share.view_count} vistas`,
    `${share.download_count} descargas`,
  ];

  if (share.expires_at) {
    parts.push(`vence el ${formatDate(share.expires_at)}`);
  }
  return parts.join(' · ');
}

function formatDate(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('es-ES');
}
